import React, { useState } from 'react';
import { Box, Chip, Collapse, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import PropTypes from 'prop-types';

const useStyles = makeStyles(() => ({
  card: {
    padding: 24,
    background: 'white',
    borderRadius: 16,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    marginBottom: 20,
  },
  sectionHeading: {
    fontWeight: 700,
    fontSize: 20,
    color: '#58595B',
    marginBottom: 4,
  },
  sectionDesc: {
    fontSize: 13,
    color: '#999',
    marginBottom: 16,
  },
  recRow: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '12px 0',
    borderBottom: '1px solid #f3f4f6',
    cursor: 'pointer',
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      background: '#fafafa',
    },
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 16,
    marginBottom: 4,
  },
  categoryIcon: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: '#fff',
    flexShrink: 0,
  },
  expandedDesc: {
    background: '#f8f9fa',
    borderRadius: 8,
    padding: '10px 14px',
    margin: '8px 0 4px 36px',
    fontSize: 13,
    lineHeight: 1.6,
    color: '#555',
  },
}));

const PRIORITY_STYLES = {
  high: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
  medium: { background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' },
  low: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
};

const CATEGORY_CONFIG = {
  page_creation: { label: 'Pages to Create', icon: '+', color: '#dc2626' },
  page_update: { label: 'Pages to Update', icon: '↑', color: '#ea580c' },
  location_page: { label: 'Location Pages', icon: 'L', color: '#f59e0b' },
  content_opportunity: { label: 'Content Opportunities', icon: 'W', color: '#10b981' },
  outrank_competitor: { label: 'Outrank Competitors', icon: 'T', color: '#7c3aed' },
  citation_building: { label: 'Citation Building', icon: 'C', color: '#3b82f6' },
  authority_gap: { label: 'Authority Gap', icon: 'A', color: '#e11d48' },
  backlink_gap: { label: 'Backlink Gap', icon: 'B', color: '#be185d' },
  backlink_reclamation: { label: 'Backlink Reclamation', icon: 'R', color: '#9f1239' },
  technical_seo: { label: 'Technical SEO for AI', icon: 'S', color: '#64748b' },
  reputation_reviews: { label: 'Reputation & Reviews', icon: 'R', color: '#ea580c' },
};

const AiRecommendations = ({ recommendations }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState({});

  if (!recommendations || recommendations.length === 0) return null;

  const toggleExpand = (idx) => {
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Group by category, preserving priority sort order
  const grouped = [];
  const seen = new Set();
  recommendations.forEach((rec) => {
    if (!seen.has(rec.category)) {
      seen.add(rec.category);
      grouped.push({
        category: rec.category,
        items: recommendations
          .map((r, i) => ({ ...r, idx: i }))
          .filter((r) => r.category === rec.category),
      });
    }
  });

  return (
    <>
      <Typography className={classes.sectionHeading} style={{ marginTop: 8 }}>
        Recommendations
      </Typography>
      <Typography className={classes.sectionDesc}>
        Actionable steps to improve your AI visibility based on your current performance data.
      </Typography>

      <Paper className={classes.card}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography style={{ fontSize: 13, color: '#666' }}>
            {recommendations.length} recommendation{recommendations.length !== 1 ? 's' : ''} found
          </Typography>
          <Box display="flex" style={{ gap: 6 }}>
            {['high', 'medium', 'low'].map((p) => {
              const count = recommendations.filter((r) => r.priority === p).length;
              if (!count) return null;
              return (
                <Chip
                  key={p}
                  label={`${count} ${p}`}
                  size="small"
                  style={{
                    ...PRIORITY_STYLES[p],
                    fontSize: 11,
                    fontWeight: 600,
                    height: 22,
                    textTransform: 'capitalize',
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {grouped.map((group) => {
          const config = CATEGORY_CONFIG[group.category] || {
            label: group.category, icon: '?', color: '#999',
          };
          return (
            <div key={group.category}>
              <div className={classes.categoryHeader}>
                <div className={classes.categoryIcon} style={{ background: config.color }}>
                  {config.icon}
                </div>
                <Typography style={{ fontSize: 13, fontWeight: 700, color: '#58595B' }}>
                  {config.label}
                </Typography>
              </div>

              {group.items.map((rec) => (
                <div key={rec.idx}>
                  <div
                    className={classes.recRow}
                    onClick={() => toggleExpand(rec.idx)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') toggleExpand(rec.idx); }}
                  >
                    <Chip
                      label={rec.priority}
                      size="small"
                      style={{
                        ...PRIORITY_STYLES[rec.priority],
                        fontSize: 10,
                        fontWeight: 700,
                        height: 20,
                        minWidth: 52,
                        textTransform: 'capitalize',
                        marginRight: 12,
                        flexShrink: 0,
                        marginTop: 2,
                      }}
                    />
                    <Box flex={1}>
                      <Typography style={{ fontSize: 13, color: '#333' }}>
                        <strong style={{ color: '#0d9373' }}>{rec.action_verb}</strong>
                        {' \u2014 '}
                        {rec.title}
                      </Typography>
                    </Box>
                    {expanded[rec.idx]
                      ? <ExpandLessIcon style={{ color: '#999', fontSize: 20 }} />
                      : <ExpandMoreIcon style={{ color: '#999', fontSize: 20 }} />}
                  </div>
                  <Collapse in={!!expanded[rec.idx]}>
                    <div className={classes.expandedDesc}>
                      <Typography variant="body2" style={{ fontSize: 13, color: '#555', marginBottom: rec.checklist ? 8 : 0 }}>
                        {rec.description}
                      </Typography>
                      {rec.checklist && rec.checklist.length > 0 && (
                        <Box style={{ marginTop: 4 }}>
                          <Typography style={{ fontSize: 11, fontWeight: 700, color: '#333', marginBottom: 4 }}>
                            Action Items:
                          </Typography>
                          {rec.checklist.map((item) => (
                            <Box key={item} display="flex" alignItems="flex-start" style={{ gap: 6 }} mb={0.5}>
                              <span style={{ color: '#0d9373', fontSize: 13, lineHeight: '18px', flexShrink: 0 }}>☐</span>
                              <Typography variant="caption" style={{ fontSize: 12, lineHeight: '18px', color: '#444' }}>
                                {item}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </div>
                  </Collapse>
                </div>
              ))}
            </div>
          );
        })}
      </Paper>
    </>
  );
};

AiRecommendations.propTypes = {
  recommendations: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string,
    priority: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    action_verb: PropTypes.string,
    checklist: PropTypes.arrayOf(PropTypes.string),
  })),
};

AiRecommendations.defaultProps = {
  recommendations: [],
};

export default AiRecommendations;
